import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiFileText,
  FiArrowRight,
  FiRefreshCw,
  FiSearch,
} from 'react-icons/fi';
import { taxService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatCurrency, formatDate } from '../../utils/formatters';

const TaxFilingList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: filingsData,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery('tax-filings', taxService.listFilings, {
    refetchOnWindowFocus: false,
  });

  if (isLoading) return <LoadingSpinner message="Loading filings..." />;

  const filings = filingsData?.data || [];

  const filtered = filings.filter((f) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      f.filing_id?.toLowerCase().includes(s) ||
      f.cycle?.toLowerCase().includes(s) ||
      f.status?.toLowerCase().includes(s)
    );
  });

  // Compute quick stats
  const totalFilings = filings.length;
  const submittedCount = filings.filter((f) => f.status === 'submitted').length;
  const approvedCount = filings.filter((f) => f.status === 'approved').length;
  const draftCount = filings.filter((f) => f.status === 'draft').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tax Filings</h1>
          <p className="page-subtitle">
            View, create, and submit your GST filings to the LRA
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/tax/filing/new')}
          >
            <FiPlus size={18} />
            Create Filing
          </button>
        </div>
      </div>

      {/* Quick stats */}
      {filings.length > 0 && (
        <div className="stats-grid" style={{ marginBottom: '24px' }}>
          <SmallStat
            label="Total Filings"
            value={totalFilings}
            color="#1a73e8"
          />
          <SmallStat label="Draft" value={draftCount} color="#6b7280" />
          <SmallStat
            label="Submitted"
            value={submittedCount}
            color="#1e40af"
          />
          <SmallStat
            label="Approved"
            value={approvedCount}
            color="#166534"
          />
        </div>
      )}

      {/* Search */}
      {filings.length > 0 && (
        <div className="search-bar">
          <FiSearch size={20} />
          <input
            type="text"
            placeholder="Search by filing ID, cycle, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {/* Empty state */}
      {filings.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <FiFileText size={48} />
            <h3>No filings yet</h3>
            <p>
              When you're ready, create your first GST filing from the tax
              summary page.
            </p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/tax/summary')}
              >
                View Tax Summary
              </button>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/tax/filing/new')}
              >
                <FiPlus size={18} />
                Create Filing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {filings.length > 0 && (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Filing ID</th>
                <th>Cycle</th>
                <th>Period</th>
                <th>Gross Sales</th>
                <th>Tax Due</th>
                <th>Status</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: 24 }}>
                    No filings match your search
                  </td>
                </tr>
              ) : (
                filtered.map((f) => (
                  <tr key={f.filing_id}>
                    <td>
                      <code style={{ fontSize: '12px' }}>{f.filing_id}</code>
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{f.cycle}</td>
                    <td>
                      {formatDate(f.period_start)} — {formatDate(f.period_end)}
                    </td>
                    <td>{formatCurrency(f.gross_sales || 0)}</td>
                    <td>
                      <strong>{formatCurrency(f.tax_due || 0)}</strong>
                    </td>
                    <td>
                      <StatusBadge status={f.status} />
                    </td>
                    <td>
                      {f.submitted_at ? formatDate(f.submitted_at) : '—'}
                    </td>
                    <td>
                      <button
                        className="icon-btn"
                        onClick={() => navigate(`/tax/filing/${f.filing_id}`)}
                        title="View details"
                      >
                        <FiArrowRight size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------- Helper components ----------

const SmallStat = ({ label, value, color }) => (
  <div
    className="card"
    style={{
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      borderLeft: `4px solid ${color}`,
    }}
  >
    <div
      style={{
        fontSize: '28px',
        fontWeight: 700,
        color: color,
        lineHeight: 1.2,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: 4 }}>
      {label}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    draft: { bg: '#f3f4f6', color: '#6b7280' },
    submitted: { bg: '#dbeafe', color: '#1e40af' },
    approved: { bg: '#dcfce7', color: '#166534' },
    rejected: { bg: '#fee2e2', color: '#991b1b' },
    amended: { bg: '#fef3c7', color: '#92400e' },
  };
  const c = colors[status] || colors.draft;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
};

export default TaxFilingList;
